import { useMemo } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { getReadProvider } from '../utils/ethers';
import { CONTRACTS } from '../config/contracts';
import XterraAbi from '../config/abis/Xterra.json';
import PoolsAbi  from '../config/abis/XterraPools.json';
import ERC20Abi  from '../config/abis/USDT.json';
import XTAbi     from '../config/abis/XTToken.json';

const ZERO = '0x0000000000000000000000000000000000000000';

/**
 * Contract instances.
 *   - `xterra` → main Xterra contract
 *   - `pools`  → XterraPools (null if XterraPools address is zero / undeployed)
 *   - `logic` / `storage` → aliases to `xterra` for backward-compat
 *   - `usdt`, `xt` → tokens
 */
export function useContracts() {
  const { signer, web3Provider, isCorrectNetwork, account } = useWallet();

  const read = useMemo(() => {
    const readProvider = getReadProvider();
    const xterra = new ethers.Contract(CONTRACTS.Xterra, XterraAbi, readProvider);
    const poolsAddr = CONTRACTS.XterraPools;
    const pools = (poolsAddr && poolsAddr !== ZERO)
      ? new ethers.Contract(poolsAddr, PoolsAbi, readProvider)
      : null;
    return {
      xterra,
      pools,
      logic:   xterra,
      storage: xterra,
      usdt:    new ethers.Contract(CONTRACTS.USDT, ERC20Abi, readProvider),
      xt:      new ethers.Contract(CONTRACTS.XT,   XTAbi,    readProvider),
    };
  }, []);

  const write = useMemo(() => {
    if (!signer || !account || !isCorrectNetwork) return null;
    const xterra = new ethers.Contract(CONTRACTS.Xterra, XterraAbi, signer);
    const poolsAddr = CONTRACTS.XterraPools;
    const pools = (poolsAddr && poolsAddr !== ZERO)
      ? new ethers.Contract(poolsAddr, PoolsAbi, signer)
      : null;
    return {
      xterra,
      pools,
      logic:   xterra,
      storage: xterra,
      usdt:    new ethers.Contract(CONTRACTS.USDT, ERC20Abi, signer),
      xt:      new ethers.Contract(CONTRACTS.XT,   XTAbi,    signer),
    };
  }, [signer, account, isCorrectNetwork]);

  return { read, write, web3Provider };
}
